import React, { useCallback, useEffect, useState } from 'react';
import Navbar from '../../ui/Navbar';
import Sidebar from '../../Sidebar';
import BrainPanel from '../../BrainPanel';
import MemoryView from '../../MemoryView';
import { canViewBrainMemory } from '../../../utils/canViewBrainMemory';
import SaveIndicator from '../../SaveIndicator';
import ToastContainer from '../../Toast';
import BusyOverlay from '../../BusyOverlay';
import LogoLoader from '../../ui/LogoLoader';
import MissionToSidebarDragGhost from '../../MissionToSidebarDragGhost';
import { MainContent } from '../MainContent';
import { AppModals } from './AppModals';
import { VIEWS } from '../constants';
import { AppMainOverlayProvider } from '../../../context/AppMainOverlayContext';
import { BrainCanvasProvider } from '../../../context/BrainCanvasContext';
import { OpenableValueLayer } from '../../../hooks/useOpenableHoverTip';
import { SAVE_INDICATOR_EVENT } from '../../../utils/saveIndicatorBridge';
import { useAuth } from '../../AuthProvider';
import { useSubscription } from '../../../context/SubscriptionContext';
import { workspaceNeedsPlan } from '../../../utils/workspaceAccess';
import Paywall from '../../Paywall';

export function AppShell(props) {
  const [overlayHost, setOverlayHost] = useState(null);
  const [bridgeSaveStatus, setBridgeSaveStatus] = useState('idle');
  const {
    sidebarCollapsed,
    setSidebarCollapsed,
    showLoadingOverlay,
    sprints,
    templates,
    saveStatus,
    isNesting,
    nestingComplete,
    dataLoading,
    isLoadingViewData,
    isNestedDataLoading,
    currentView,
    selectedSprintId,
    selectedTemplateId,
    activeView,
    sprintOrder,
    user,
    workspaceId,
    workspaces,
    isInitialStartupComplete,
    isLoadingItem,
    itemLoadErrors,
    multiCampaignData,
    selectedSprint,
    selectedTemplate,
    preferences,
    flowViewVisibility,
    hideViewExplainerModal,
    preferredDetailView,
    pendingMissionFocus,
    removingCampaignId,
    handleTabClickWithUrl,
    handleNewTab,
    handleHistoryNavigate,
    handleSidebarNavigate,
    handleSelectSprintWithUrl,
    handleSelectTemplateWithUrl,
    openCreateSprintModal,
    openCreateNestedSprintModal,
    openCreateTemplateModal,
    handleRenameSprint,
    handleSprintIconChange,
    handleDeleteSprint,
    handleRestoreSprint,
    handlePermanentlyDeleteSprint,
    handleEmptyTrash,
    handleRenameTemplate,
    handleDuplicateTemplate,
    handleUseTemplate,
    handleTemplateIconChange,
    handleCreateTemplateFromCampaign,
    handleNestCampaignWithNotification,
    handleReorderNestedCampaign,
    openSettingsModal,
    handleOpenDocsWithUrl,
    openWorkspaceArea,
    openDashboardsArea,
    setSprintOrder,
    handleSelectViewWithUrl,
    handleOpenInNewTab,
    logout,
    setWorkspace,
    setFlowViewVisibility,
    setHideViewExplainerModal,
    handleAddSprint,
    handleUpdateSprint,
    handleRemoveCampaignFromView,
    handleAddCampaignToView,
    handleRenameView,
    handleViewIconChange,
    handleUpdateTemplate,
    clearPreferredDetailView,
    handleMissionFocused,
    handleViewModeChange,
    handleCustomViewChange,
    isCreateSprintOpen,
    closeCreateSprintModal,
    onSprintCreated,
    preselectedTemplate,
    parentSprintIdForNesting,
    loadFullSprint,
    isCreateTemplateOpen,
    closeCreateTemplateModal,
    onTemplateCreated,
    isSettingsOpen,
    closeSettingsModal,
    updatePreferences,
  } = props;

  const { pendingInvites } = useAuth();
  const { complimentary, hasAccess, loading: subLoading } = useSubscription();
  const needsPlan = workspaceNeedsPlan(
    complimentary,
    workspaceId,
    workspaces,
    hasAccess,
    pendingInvites
  );

  const [brainOpen, setBrainOpen] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const canViewMemory = canViewBrainMemory({
    complimentary,
    hostname: typeof window !== 'undefined' ? window.location.hostname : '',
  });
  const [sidebarArea, setSidebarArea] = useState(
    currentView === VIEWS.DOCS
      ? 'docs'
      : currentView === VIEWS.DASHBOARDS
        ? 'dashboards'
        : 'workspace'
  );

  useEffect(() => {
    if (currentView === VIEWS.DOCS) setSidebarArea('docs');
    else if (currentView === VIEWS.DASHBOARDS) setSidebarArea('dashboards');
    else setSidebarArea('workspace');
  }, [currentView]);

  useEffect(() => {
    document.body.classList.toggle('docs-view', currentView === VIEWS.DOCS);
    return () => {
      document.body.classList.remove('docs-view');
    };
  }, [currentView]);

  useEffect(() => {
    let savedTimer = 0;
    const onStatus = (e) => {
      const status = e.detail?.status;
      if (!status) return;
      window.clearTimeout(savedTimer);
      setBridgeSaveStatus(status);
      if (status === 'saved') {
        savedTimer = window.setTimeout(() => setBridgeSaveStatus('idle'), 2000);
      }
    };
    window.addEventListener(SAVE_INDICATOR_EVENT, onStatus);
    return () => {
      window.removeEventListener(SAVE_INDICATOR_EVENT, onStatus);
      window.clearTimeout(savedTimer);
    };
  }, []);

  const closeBrain = useCallback(() => setBrainOpen(false), []);
  const closeMemory = useCallback(() => setMemoryOpen(false), []);
  const openMemory = useCallback(() => {
    if (canViewMemory) setMemoryOpen(true);
  }, [canViewMemory]);
  const handleToggleBrain = useCallback(() => {
    setBrainOpen((open) => !open);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        handleToggleBrain();
      }
      if (
        canViewMemory &&
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        e.key.toLowerCase() === 'm'
      ) {
        e.preventDefault();
        setMemoryOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleToggleBrain, canViewMemory]);

  return (
    <AppMainOverlayProvider host={overlayHost}>
      <div className={`app ${sidebarCollapsed ? 'app--sidebar-collapsed' : ''} ${brainOpen ? 'app--brain-open' : ''}`}>
        <OpenableValueLayer />
        <div className={`app-loading-overlay ${!showLoadingOverlay ? 'app-loading-overlay--hidden' : ''}`}>
          <LogoLoader size={120} />
        </div>

        <Navbar
          sprints={sprints}
          onTabClick={handleTabClickWithUrl}
          onNewTab={handleNewTab}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={() => setSidebarCollapsed((c) => !c)}
          onHistoryNavigate={handleHistoryNavigate}
          brainOpen={brainOpen}
          onToggleBrain={handleToggleBrain}
          onOpenSettings={openSettingsModal}
          workspaceId={workspaceId}
          workspaces={workspaces}
          setWorkspace={setWorkspace}
        />
        <SaveIndicator
          status={
            saveStatus === 'saving' || bridgeSaveStatus === 'saving'
              ? 'saving'
              : saveStatus === 'saved' || bridgeSaveStatus === 'saved'
                ? 'saved'
                : saveStatus
          }
          isNesting={isNesting}
          nestingComplete={nestingComplete && !isNesting}
        />
        <ToastContainer />
        <BusyOverlay />
        <MissionToSidebarDragGhost />

        <BrainCanvasProvider>
        <div className={`app-layout ${sidebarCollapsed ? 'app-layout--sidebar-collapsed' : ''}`}>
          <Sidebar
            deferOrphanDetection={dataLoading || isLoadingViewData || showLoadingOverlay || isNestedDataLoading}
            currentView={currentView}
            onNavigate={handleSidebarNavigate}
            sprints={sprints}
            selectedSprintId={selectedSprintId}
            selectedTemplateId={selectedTemplateId}
            onSelectSprint={handleSelectSprintWithUrl}
            onSelectTemplate={handleSelectTemplateWithUrl}
            onCreateSprint={() => openCreateSprintModal()}
            onCreateNestedSprint={openCreateNestedSprintModal}
            onCreateTemplate={openCreateTemplateModal}
            onRenameSprint={handleRenameSprint}
            onSprintIconChange={handleSprintIconChange}
            onDeleteSprint={handleDeleteSprint}
            loadFullSprint={loadFullSprint}
            onRestoreSprint={handleRestoreSprint}
            onPermanentlyDeleteSprint={handlePermanentlyDeleteSprint}
            onEmptyTrash={handleEmptyTrash}
            onRenameTemplate={handleRenameTemplate}
            onDeleteTemplate={handleDeleteSprint}
            onRestoreTemplate={handleRestoreSprint}
            onPermanentlyDeleteTemplate={handlePermanentlyDeleteSprint}
            onDuplicateTemplate={handleDuplicateTemplate}
            onUseTemplate={handleUseTemplate}
            onTemplateIconChange={handleTemplateIconChange}
            onCreateTemplateFromCampaign={handleCreateTemplateFromCampaign}
            onNestCampaign={handleNestCampaignWithNotification}
            onReorderNestedCampaign={handleReorderNestedCampaign}
            isCollapsed={sidebarCollapsed}
            onToggleSidebar={() => setSidebarCollapsed((c) => !c)}
            onHistoryNavigate={handleHistoryNavigate}
            appName="Fokus"
            onOpenDocs={handleOpenDocsWithUrl}
            onOpenWorkspaceArea={openWorkspaceArea}
            onOpenDashboards={openDashboardsArea}
            sidebarArea={sidebarArea}
            sprintOrder={sprintOrder}
            onReorderSprints={setSprintOrder}
            activeView={activeView}
            onSelectView={handleSelectViewWithUrl}
            onOpenInNewTab={handleOpenInNewTab}
            user={user}
            onLogout={logout}
            workspaceId={workspaceId}
            workspaces={workspaces}
            setWorkspace={setWorkspace}
            onOpenSettings={openSettingsModal}
            brainOpen={brainOpen}
            onToggleBrain={handleToggleBrain}
          />

          <main
            ref={setOverlayHost}
            className={`app-main ${sidebarCollapsed ? 'app-main--sidebar-collapsed' : ''} ${currentView === VIEWS.DOCS ? 'app-main--docs' : ''}`}
          >
            <div className="app-main__body">
              {isInitialStartupComplete && !subLoading && needsPlan ? (
                <Paywall embedded />
              ) : isInitialStartupComplete && !subLoading ? (
                <MainContent
                  isLoadingItem={isLoadingItem}
                  itemLoadErrors={itemLoadErrors}
                  currentView={currentView}
                  VIEWS={VIEWS}
                  activeView={activeView}
                  isLoadingViewData={isLoadingViewData}
                  multiCampaignData={multiCampaignData}
                  selectedSprint={selectedSprint}
                  selectedTemplate={selectedTemplate}
                  sprints={sprints}
                  preferences={preferences}
                  flowViewVisibility={flowViewVisibility}
                  setFlowViewVisibility={setFlowViewVisibility}
                  hideViewExplainerModal={hideViewExplainerModal}
                  setHideViewExplainerModal={setHideViewExplainerModal}
                  handleSelectSprint={handleSelectSprintWithUrl}
                  handleSelectTemplate={handleSelectTemplateWithUrl}
                  handleSelectView={handleSelectViewWithUrl}
                  handleAddSprint={handleAddSprint}
                  handleUpdateSprint={handleUpdateSprint}
                  handleRenameSprint={handleRenameSprint}
                  handleSprintIconChange={handleSprintIconChange}
                  handleDeleteSprint={handleDeleteSprint}
                  handleNestCampaignWithNotification={handleNestCampaignWithNotification}
                  loadFullSprint={loadFullSprint}
                  handleRenameView={handleRenameView}
                  handleViewIconChange={handleViewIconChange}
                  handleRemoveCampaignFromView={handleRemoveCampaignFromView}
                  handleAddCampaignToView={handleAddCampaignToView}
                  removingCampaignId={removingCampaignId}
                  handleUseTemplate={handleUseTemplate}
                  handleUpdateTemplate={handleUpdateTemplate}
                  handleRenameTemplate={handleRenameTemplate}
                  handleTemplateIconChange={handleTemplateIconChange}
                  preferredDetailView={preferredDetailView}
                  pendingMissionFocus={pendingMissionFocus}
                  clearPreferredDetailView={clearPreferredDetailView}
                  clearPendingMissionFocus={handleMissionFocused}
                  onViewModeChange={handleViewModeChange}
                  onCustomViewChange={handleCustomViewChange}
                  openCreateSprintModal={openCreateSprintModal}
                  openCreateNestedSprintModal={openCreateNestedSprintModal}
                  handleOpenInNewTab={handleOpenInNewTab}
                  sprintOrder={sprintOrder}
                  openWorkspaceArea={openWorkspaceArea}
                  sidebarCollapsed={sidebarCollapsed}
                />
              ) : null}
            </div>
          </main>

          <BrainPanel
            isOpen={brainOpen}
            onClose={closeBrain}
            canViewMemory={canViewMemory}
            onOpenMemory={openMemory}
            workspaceId={workspaceId}
            campaignId={selectedSprintId}
          />
          <MemoryView
            isOpen={memoryOpen && canViewMemory}
            onClose={closeMemory}
            workspaceId={workspaceId}
          />
        </div>
        </BrainCanvasProvider>

        <AppModals
          isCreateSprintOpen={isCreateSprintOpen}
          closeCreateSprintModal={closeCreateSprintModal}
          onSprintCreated={onSprintCreated}
          preselectedTemplate={preselectedTemplate}
          parentSprintIdForNesting={parentSprintIdForNesting}
          templates={templates}
          sprints={sprints}
          loadFullSprint={loadFullSprint}
          isCreateTemplateOpen={isCreateTemplateOpen}
          closeCreateTemplateModal={closeCreateTemplateModal}
          onTemplateCreated={onTemplateCreated}
          isSettingsOpen={isSettingsOpen}
          closeSettingsModal={closeSettingsModal}
          preferences={preferences}
          updatePreferences={updatePreferences}
        />
      </div>
    </AppMainOverlayProvider>
  );
}
